import { motion } from 'framer-motion';
import { ChevronRight, Shield, RotateCcw, CreditCard, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';

const ReturnPolicyPage = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <div className="py-24 px-6 md:px-12 lg:px-24 border-b border-white/5">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 font-body text-sm text-white/50 mb-6">
            <Link to="/" className="hover:text-white transition-colors">Acasă</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">Politica de Retur</span>
          </div>
          
          <h1 className="font-heading font-bold text-4xl md:text-6xl tracking-tight uppercase text-white" data-testid="return-policy-title">
            POLITICA DE RETUR
          </h1>
          <p className="font-body text-white/50 mt-4 max-w-2xl">
            Satisfacția ta este prioritatea noastră. Oferim o politică de retur fără bătăi de cap, în conformitate cu legislația română.
          </p>
        </motion.div>
      </div>

      {/* Content */}
      <div className="px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-4xl">
          {/* Main Policy */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-16"
          >
            <div className="bg-[#121212] border border-white/5 p-8 md:p-12">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-[#c00000]/10 flex items-center justify-center">
                  <RotateCcw className="w-6 h-6 text-[#c00000]" />
                </div>
                <h2 className="font-heading font-bold text-2xl text-white">Garanție Retur în 14 Zile</h2>
              </div>
              
              <p className="font-body text-white/70 leading-relaxed text-lg">
                Conform legislației române (OUG 34/2014), produsele achiziționate online pot fi returnate în termen de <strong className="text-white">14 zile calendaristice</strong> fără a fi necesar să se precizeze motivul, cu condiția ca acestea să fie în starea originală, cu etichetele și ambalajul intact.
              </p>
              
              <p className="font-body text-white/70 leading-relaxed text-lg mt-4">
                Costul de transport pentru retur este suportat de client, iar rambursarea se face de obicei prin aceeași metodă de plată folosită la achiziție.
              </p>
            </div>
          </motion.section>

          {/* Key Points */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-16"
          >
            <h2 className="font-heading font-bold text-xl tracking-wider text-white mb-8">CONDIȚII DE RETUR</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-[#121212] border border-white/5 p-6">
                <h3 className="font-heading font-bold text-white mb-3">Stare Originală</h3>
                <p className="font-body text-sm text-white/60">
                  Produsele trebuie să fie nepurtate, nespălate și în aceeași stare în care au fost primite. Toate etichetele originale trebuie să fie atașate.
                </p>
              </div>
              
              <div className="bg-[#121212] border border-white/5 p-6">
                <h3 className="font-heading font-bold text-white mb-3">Ambalaj Original</h3>
                <p className="font-body text-sm text-white/60">
                  Articolele trebuie returnate în cutia și ambalajul original. Ambalajul deteriorat poate afecta returnarea.
                </p>
              </div>
              
              <div className="bg-[#121212] border border-white/5 p-6">
                <h3 className="font-heading font-bold text-white mb-3">14 Zile Calendaristice</h3>
                <p className="font-body text-sm text-white/60">
                  Perioada de retur începe din ziua în care primești comanda. Retururile trebuie inițiate în acest interval de timp.
                </p>
              </div>
              
              <div className="bg-[#121212] border border-white/5 p-6">
                <h3 className="font-heading font-bold text-white mb-3">Costuri Transport</h3>
                <p className="font-body text-sm text-white/60">
                  Costurile de transport pentru retur sunt responsabilitatea clientului. Recomandăm folosirea unui serviciu de curierat cu urmărire.
                </p>
              </div>
            </div>
          </motion.section>

          {/* How to Return */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-16"
          >
            <h2 className="font-heading font-bold text-xl tracking-wider text-white mb-8">CUM SĂ RETURNEZI</h2>
            
            <div className="space-y-6">
              <div className="flex gap-6 items-start">
                <div className="w-10 h-10 bg-[#c00000] flex items-center justify-center flex-shrink-0">
                  <span className="font-heading font-bold text-white">1</span>
                </div>
                <div>
                  <h3 className="font-heading font-bold text-white mb-2">Contactează-ne</h3>
                  <p className="font-body text-white/60">
                    Contactează-ne pe WhatsApp (+40 759 649 614) sau email pentru a iniția returnarea. Furnizează numărul comenzii și motivul returnării.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-6 items-start">
                <div className="w-10 h-10 bg-[#c00000] flex items-center justify-center flex-shrink-0">
                  <span className="font-heading font-bold text-white">2</span>
                </div>
                <div>
                  <h3 className="font-heading font-bold text-white mb-2">Împachetează Produsele</h3>
                  <p className="font-body text-white/60">
                    Împachetează cu grijă articolele în ambalajul original. Include toate etichetele și accesoriile.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-6 items-start">
                <div className="w-10 h-10 bg-[#c00000] flex items-center justify-center flex-shrink-0">
                  <span className="font-heading font-bold text-white">3</span>
                </div>
                <div>
                  <h3 className="font-heading font-bold text-white mb-2">Trimite Coletul</h3>
                  <p className="font-body text-white/60">
                    Trimite coletul la adresa noastră de retur. Recomandăm folosirea unui serviciu de curierat cu urmărire pentru protecția ta.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-6 items-start">
                <div className="w-10 h-10 bg-[#c00000] flex items-center justify-center flex-shrink-0">
                  <span className="font-heading font-bold text-white">4</span>
                </div>
                <div>
                  <h3 className="font-heading font-bold text-white mb-2">Primești Rambursarea</h3>
                  <p className="font-body text-white/60">
                    După ce primim și inspectăm returul, vom procesa rambursarea în 5-7 zile lucrătoare către metoda de plată originală.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Refund Information */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-16"
          >
            <h2 className="font-heading font-bold text-xl tracking-wider text-white mb-8">INFORMAȚII RAMBURSARE</h2>
            
            <div className="bg-[#121212] border border-white/5 p-8">
              <div className="flex items-center gap-4 mb-6">
                <CreditCard className="w-6 h-6 text-[#c00000]" />
                <h3 className="font-heading font-bold text-white">Metode de Plată</h3>
              </div>
              <ul className="space-y-3 font-body text-white/70">
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-[#c00000]"></span>
                  <span><strong className="text-white">Plăți cu Cardul:</strong> Rambursare procesată pe același card în 5-7 zile lucrătoare</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-[#c00000]"></span>
                  <span><strong className="text-white">Plata la Livrare:</strong> Rambursare prin transfer bancar în 5-7 zile lucrătoare</span>
                </li>
              </ul>
            </div>
          </motion.section>

          {/* Contact */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="bg-[#c00000]/10 border border-[#c00000]/20 p-8 text-center">
              <Phone className="w-8 h-8 text-[#c00000] mx-auto mb-4" />
              <h2 className="font-heading font-bold text-xl text-white mb-2">Ai Nevoie de Ajutor?</h2>
              <p className="font-body text-white/70 mb-4">
                Contactează-ne pe WhatsApp pentru asistență rapidă cu returnarea ta.
              </p>
              <a 
                href="https://wa.me/40759649614"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-[#25D366] text-white px-8 py-3 font-heading tracking-wider hover:bg-[#20BA5A] transition-colors"
              >
                +40 759 649 614
              </a>
            </div>
          </motion.section>
        </div>
      </div>
    </div>
  );
};

export default ReturnPolicyPage;
